import { Alert, AlertDescription, cn } from '@up2stay/up2promo-ui';
import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

import { SidebarInset } from '@/core/components/navigation/sidebar';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePendingInvitations } from '@/core/hooks/use-pending-invitations';
import { usePushNotifications } from '@/core/hooks/use-push-notifications';
import { usePushPermissionSync } from '@/core/hooks/use-push-permission-sync';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { AuthStatus, useAuthStore } from '@/core/store/auth.store';

import { AppSidebar } from './navigation/app-sidebar';
import { ShellRealtimeSync } from './shell-realtime-sync';
import { ImpersonationBanner } from './support/impersonation-banner';

export function AppShell({ children }: { children: ReactNode }) {
  const { navigateShell } = useNavigateShell();
  const dict = useDictionary();
  const authStatus = useAuthStore((state) => state.authStatus);
  const { data: pendingInvitations, isLoading } = usePendingInvitations();
  const hasShownToast = useRef(false);

  usePushNotifications();
  usePushPermissionSync();

  useEffect(() => {
    // Only show toast once when invitations are loaded and there are pending invitations
    if (
      !isLoading &&
      pendingInvitations &&
      pendingInvitations.length > 0 &&
      !hasShownToast.current
    ) {
      hasShownToast.current = true;
      const count = pendingInvitations.length;
      const invitationText =
        count === 1
          ? dict.workspace.invitation.toast.single
          : dict.workspace.invitation.toast.multiple.replace(
              '{count}',
              String(count),
            );

      toast.info(invitationText, {
        action: {
          label: dict.workspace.invitation.toast.view,
          onClick: () =>
            navigateShell(
              `${shellPaths.moreUserNotifications}?tab=invitations`,
            ),
        },
        duration: 5000,
      });
    }
  }, [isLoading, pendingInvitations, dict, navigateShell]);

  return (
    <>
      <ShellRealtimeSync />
      <AppSidebar />
      <SidebarInset
        className={cn(
          '@container/content',
          'app-gradient-bg',
          // flex-col + min-h-0 so the tab outlet (flex-1 child) fills remaining height
          'flex min-h-0 flex-1 flex-col overflow-hidden',
          'overflow-x-hidden',
        )}
      >
        {authStatus === AuthStatus.OFFLINE && (
          <div className="px-3 pt-3">
            <Alert className="glass-surface border-amber-400/50 bg-amber-100/45 dark:bg-amber-900/15">
              <AlertDescription className="text-amber-900 dark:text-amber-100">
                {dict.auth.errors.networkError}
              </AlertDescription>
            </Alert>
          </div>
        )}
        <ImpersonationBanner />
        {children}
      </SidebarInset>
    </>
  );
}
